import { DirectorDocumentState } from "../steps/BusinessDocuments"
import { DirectorDocumentField, DirectorDocumentsUpload } from "../Types"

export const extractRequiredDocs = (data: any) => {
    return data.map((item) => ({
        id: item.businessDocument.id,
        document: item.businessDocument.document
    }))
}

export const isDocumentRequired = (documentTitle: string, requiredDocs: any) => {
    return requiredDocs.some(item => item.document?.toLowerCase() === documentTitle.toLowerCase())
}

const updateDirectorDocumentState = (
    directorId: number,
    documentId: string,
    updates: Partial<DirectorDocumentState>,
    setDirectorDocumentStates
  ) => {
    setDirectorDocumentStates((prevStates) => ({
      ...prevStates,
      [directorId]: {
        ...(prevStates[directorId] || {}),
        [documentId]: {
          ...(prevStates[directorId]?.[documentId] || {
            file: null,
            isSaved: false,
            uploading: false,
            error: null,
          }),
          ...updates,
        },
      },
    }));
  };

 export const handleDirectorFileSelect = (
    directorId: number,
    documentId: string,
    setDirectorDocumentStates: any,
  ) => (file: File) => {
    updateDirectorDocumentState(directorId, documentId, {
      file,
      isSaved: false,
      uploading: false,
      error: null,
    }, setDirectorDocumentStates);
  };

  export const handleDirectorFileDelete = (
    directorId: number,
    documentId: string,
    setDirectorDocumentStates
  ) => () => {
    updateDirectorDocumentState(directorId, documentId, {
      file: null,
      isSaved: false,
      uploading: false,
      error: null,
    }, setDirectorDocumentStates);
  };

  const directorDocumentFileFields: Record<string, DirectorDocumentField> = {
    id: "idDocument",
    address: "proofOfAddress",
    photo: "photograph",
  };

  const directorDocumentSavedFields: Record<string, { flag: string; url: string }> = {
    id: { flag: "hasIdDocument", url: "idUrl" },
    address: { flag: "hasProofOfAddress", url: "poaUrl" },
    photo: { flag: "hasPhotograph", url: "photographUrl" },
  };

  export const isDirectorDocumentSaved = (director: any, documentId: string): boolean => {
    const fields = directorDocumentSavedFields[documentId];
    return Boolean(fields && (director?.[fields.flag] || director?.[fields.url]));
  };

  export const buildDirectorDocumentsUpload = (
    directors: any[],
    directorDocumentStates: Record<number, Record<string, DirectorDocumentState>>,
  ): DirectorDocumentsUpload[] =>
    directors.map(({
      poaUrl, idUrl, photographUrl,
      hasIdDocument, hasProofOfAddress, hasPhotograph,
      ...director
    }) => {
      const files: DirectorDocumentsUpload["files"] = {};
      Object.entries(directorDocumentFileFields).forEach(([documentId, field]) => {
        const documentState = directorDocumentStates[director.id]?.[documentId];
        if (documentState?.file && !documentState.isSaved) {
          files[field] = documentState.file;
        }
      });
      return { director, files };
    });

  export const getDirectorDocumentState = (
    directorId: number,
    documentId: string,
    directorDocumentStates
  ): DirectorDocumentState => {
    return (
      directorDocumentStates[directorId]?.[documentId] || {
        file: null,
        isSaved: false,
        uploading: false,
        error: null,
      }
    );
  };

  export const extractFileNameFromUrl = (
    url: string, 
  ): string | null => {
    try {
      const decodedUrl = decodeURIComponent(url);
      const match = decodedUrl.match(/\/([^/?]+)(\?|$)/)
      return match ? match[1] : null;
    } catch (error) {
      console.error('Error extracting file name:', error);
      return null;
    }
  };

  export const getMostRecentUploadedDocs = (uploadedDocuments: any[], requiredDocuments: any[]) => {
    const recentDocsMap = new Map();
    
    const requiredDocIds = requiredDocuments?.map(doc => doc.id) || [];
  
    uploadedDocuments.forEach((doc) => {
      if (requiredDocIds.includes(doc.businessDocument.id)) {
        const { createdAt, businessDocument, directorName } = doc;
        const uniqueKey = `${businessDocument.id}-${directorName || ''}`;
      
        const storedDoc = recentDocsMap.get(uniqueKey);
        const parsedDate = new Date(createdAt);
      
        if (!storedDoc || parsedDate > storedDoc.storedDate) {
          recentDocsMap.set(uniqueKey, {
            doc,
            storedDate: parsedDate
          });
        }
      }
    });
    
    const mostRecentDocs = Array.from(recentDocsMap.values()).map((item) => item.doc);
  
    const previousDocsForCurrentBizType: any[] = [];
    const missingDocuments: any[] = [];
    
    requiredDocuments?.forEach((requiredDoc) => {
      const exists = mostRecentDocs.some(
        uploadedDoc => uploadedDoc.businessDocument.id === requiredDoc.id
      );
  
      if (exists) {
        previousDocsForCurrentBizType.push(
          ...mostRecentDocs.filter(uploadedDoc => uploadedDoc.businessDocument.id === requiredDoc.id)
        );
      } else {
        missingDocuments.push({
          businessDocument: {
            id: requiredDoc.id,
            document: requiredDoc.document,
            description: requiredDoc.description || requiredDoc.document,
            type: "document" 
          },
          documentApprovalStatus: "NOT UPLOADED"
        });
      }
    });
    
    return [...previousDocsForCurrentBizType, ...missingDocuments];
  };

  export const getDirectorId = (directorName, directorsList) => {
    if (directorsList) {
      const director = directorsList.find((doc) => doc.name === directorName)
      return director?.id
    }
  }